/**
 * Brand Palette Constants — "Warm Editorial"
 *
 * Single source of truth for the raw hex values that have to exist in
 * JavaScript rather than CSS. `app/globals.css` remains the canonical token
 * layer for anything render-able through Tailwind utilities; this module
 * exists only for the places a CSS custom property cannot reach:
 *
 * - Amplify's `ThemeProvider` token object (`client/components/auth/AuthScreen.tsx`)
 * - `app/global-error.tsx`, which renders its own `<html>` outside the root
 *   layout and therefore cannot rely on `globals.css` being loaded
 * - Organization branding fallbacks when a row has no colors set
 *
 * Keep these values in sync with the matching `--color-*` tokens in
 * `app/globals.css`. Do not add new consumers that could use a Tailwind class
 * or a CSS variable instead.
 */

/**
 * Espresso primary. Mirrors `--color-espresso-*` in app/globals.css.
 */
export const BRAND_ESPRESSO = {
  50: "#F1ECE5",
  100: "#E3DCD2",
  200: "#BFB5A9",
  300: "#93877B",
  400: "#6B5F55",
  500: "#4E443C",
  600: "#3B322C",
  700: "#2E2722",
  800: "#26201C",
  900: "#1B1714",
  950: "#120F0D",
} as const;

/** Cream ground and warm ink. Mirrors `--color-warm-*`. */
export const BRAND_WARM = {
  50: "#FBF7EF",
  100: "#F4EEE2",
  200: "#E9E1D2",
  300: "#D9CFBC",
  400: "#BDB2A0",
  500: "#9A9083",
  600: "#7E7466",
  700: "#6B6257",
  800: "#4A433B",
  900: "#312C26",
  950: "#221F1A",
} as const;

/** Decorative only: hairlines and large accents, never body copy under 18px. */
export const BRAND_GOLD = {
  50: "#FBF6E8",
  100: "#F6ECD1",
  200: "#EFDDAE",
  300: "#E3C489",
  400: "#D6B270",
  500: "#C9A863",
  600: "#A78A4C",
  700: "#856D3B",
  800: "#5F4E2B",
  900: "#3E3319",
  950: "#26200F",
} as const;

/** Semantic surface + text values. Mirrors the `:root` semantic block. */
export const BRAND_SEMANTIC = {
  background: BRAND_WARM[50],
  surface: "#FFFDF8",
  textPrimary: BRAND_WARM[950],
  textSecondary: BRAND_WARM[700],
  textMuted: BRAND_WARM[700],
  primary: BRAND_ESPRESSO[600],
  primaryForeground: "#FFFFFF",
  secondary: BRAND_WARM[100],
  secondaryForeground: BRAND_WARM[700],
  muted: BRAND_WARM[100],
  accent: BRAND_WARM[100],
  accentForeground: BRAND_ESPRESSO[700],
  border: BRAND_WARM[200],
  borderLight: BRAND_WARM[100],
  ring: BRAND_ESPRESSO[600],
  overlay: "rgba(34, 31, 26, 0.5)",
} as const;

/**
 * Default organization branding colors, applied when an organization row has
 * no explicit primary/secondary color.
 *
 * Typed as `string` (not the literal) so they can seed mutable form state that
 * later holds an operator-entered color.
 */
export const DEFAULT_ORG_PRIMARY_COLOR: string = BRAND_ESPRESSO[600];
export const DEFAULT_ORG_SECONDARY_COLOR: string = BRAND_ESPRESSO[700];

/**
 * Pre-rebrand database defaults for `organizations.primary_color` and
 * `organizations.secondary_color` (see `drizzle/schema.ts`). Rows created
 * before the rebrand still carry these blues; they mean "not customized" and
 * must resolve to the brand defaults rather than render blue.
 */
export const LEGACY_ORG_PRIMARY_COLORS: readonly string[] = ["#3B82F6"];
export const LEGACY_ORG_SECONDARY_COLORS: readonly string[] = ["#1E40AF"];

/**
 * Resolve a stored organization color for rendering: the brand default when
 * the value is missing, blank, or one of the legacy database defaults
 * (compared case-insensitively); otherwise the trimmed stored value.
 */
export function resolveOrgBrandColor(
  value: string | null | undefined,
  kind: "primary" | "secondary"
): string {
  const fallback = kind === "primary" ? DEFAULT_ORG_PRIMARY_COLOR : DEFAULT_ORG_SECONDARY_COLOR;
  const legacy = kind === "primary" ? LEGACY_ORG_PRIMARY_COLORS : LEGACY_ORG_SECONDARY_COLORS;
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return fallback;
  const upper = trimmed.toUpperCase();
  if (legacy.some((color) => color.toUpperCase() === upper)) return fallback;
  return trimmed;
}
